/**
 * Live pricing preview hook (per-line) — used by the configurator.
 *
 * KAN-1642 — **jedna cesta k ceně položky v prohlížeči.** Konfigurátor
 * (hlavní cena i srovnávací karty voleb) jde přes ``linePriceQuery`` /
 * ``feasibilityQuery`` a jejich klíč je OBSAH požadavku, ne komponenta —
 * dvě místa, která se ptají na totéž, sdílí jeden dotaz. Cena se dál počítá
 * výhradně na serveru (``/pricing/line``).
 *
 * Před KAN-1642 otevření nabídky vystřelilo 7× ``/pricing/line`` a 7×
 * ``/pricing/feasibility`` naráz: každý krok seedování výchozích hodnot
 * měnil klíč a nikdo nic nerušil, takže se požadavky na serveru navzájem
 * brzdily a cena naskočila za ~10 s. Proto:
 *
 * * **ustálení** — dotaz se pustí až pro stav, který se ``PRICE_SETTLE_MS``
 *   nezměnil (seedování ani rychlé klikání nepouští požadavek za každý krok);
 * * **zrušení** — ``signal`` React Query jde až do ``fetch``, takže
 *   zastaralý požadavek skončí i v prohlížeči;
 * * ``isSettling`` — dokud se stav neustálil, volající nesmí ukazovat starou
 *   cenu jako platnou.
 */

import { hashKey, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { apiFetch } from "@/lib/api";
import type {
  AnalysisSnapshot,
  LineInput,
  LinePriceBreakdown,
  PriceFromResponse,
} from "@/api/types";

const API = "/api/v1";

// --- Live pricing preview --------------------------------------------

/** Jak dlouho se stav formuláře nesmí změnit, než se nacení. Seedování
 *  výchozích hodnot proběhne v jednotkách renderů — tohle je spolehlivě
 *  delší a zákazník to na odezvě nepozná. */
export const PRICE_SETTLE_MS = 250;

/** Hodnota, která se ``ms`` nezměnila (podle obsahu, ne identity).
 *  ``settling`` je ``true``, dokud aktuální hodnota ustálenou nedohnala. */
export function useSettled<T>(value: T, ms: number = PRICE_SETTLE_MS): {
  settled: T | null;
  settling: boolean;
} {
  const hash = value === null ? null : hashKey([value]);
  const [settled, setSettled] = useState<{ hash: string | null; value: T | null }>(
    { hash: null, value: null },
  );
  useEffect(() => {
    if (hash === null) {
      setSettled({ hash: null, value: null });
      return;
    }
    const timer = setTimeout(() => setSettled({ hash, value }), ms);
    return () => clearTimeout(timer);
    // ``value`` se mění identitou při každém renderu; rozhoduje obsah.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, ms]);
  return {
    settled: hash === null ? null : settled.value,
    settling: hash !== null && settled.hash !== hash,
  };
}

interface UsePriceLineVars extends LineInput {
  lang?: string;
  /** Dispatch the preview through this process's PricingStrategy. Omitted
   *  (laser callers) → BE defaults to laser, so existing callers are
   *  unaffected. */
  process_type?: string;
}

/** Dotaz na cenu jedné položky — sdílený všemi, kdo se na cenu ptají.
 *  Klíč je celý požadavek, takže stejné tělo = týž dotaz v cache. */
export function linePriceQuery(vars: UsePriceLineVars) {
  const { lang, process_type, ...line } = vars;
  return {
    queryKey: ["pricing", "line", process_type ?? null, lang ?? null, line] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const qs = process_type
        ? `?process_type=${encodeURIComponent(process_type)}`
        : "";
      return apiFetch<LinePriceBreakdown>(`${API}/pricing/line${qs}`, {
        method: "POST",
        body: line,
        lang,
        signal,
      });
    },
    // No retries — a 422 pricing_unavailable is a deterministic result,
    // not a transient failure.
    retry: false,
  };
}

export function usePriceLine(
  vars: UsePriceLineVars | null,
  options?: Omit<UseQueryOptions<LinePriceBreakdown>, "queryKey" | "queryFn" | "enabled">,
) {
  const { settled, settling } = useSettled(vars);
  const query = useQuery<LinePriceBreakdown>({
    ...linePriceQuery(settled ?? ({} as UsePriceLineVars)),
    enabled: settled !== null,
    // Cena musí odpovídat dnešním sazbám: stejný požadavek se sdílí, dokud
    // letí, ale znovu otevřený konfigurátor se ptá znovu.
    staleTime: 0,
    ...options,
  });
  return { ...query, isSettling: settling };
}

// --- Price-from (homepage „Hotový odhad" card) ------------------------

export interface UsePriceFromVars {
  process_type: string;
  /** Full analyzer snapshot from the upload (``Quote.analysis``) — the BE
   *  derives the DXF geometry (area / cut length / holes) server-side. */
  analysis: AnalysisSnapshot;
  lang?: string;
}

/** Cheapest-material per-piece price for an analyzed part — the "od X Kč/ks"
 *  row of the homepage estimate card. One shot per upload (staleTime ∞ within
 *  the card's lifetime); ``price_from_czk`` null = hide the row. */
export function usePriceFrom(
  vars: UsePriceFromVars | null,
  options?: Omit<UseQueryOptions<PriceFromResponse>, "queryKey" | "queryFn" | "enabled">,
) {
  return useQuery<PriceFromResponse>({
    queryKey: ["price-from", vars?.process_type, vars?.analysis, vars?.lang],
    queryFn: () => {
      if (!vars) throw new Error("usePriceFrom: vars required when enabled");
      const qs = `?process_type=${encodeURIComponent(vars.process_type)}${
        vars.lang ? `&lang=${encodeURIComponent(vars.lang)}` : ""
      }`;
      return apiFetch<PriceFromResponse>(`${API}/pricing/price-from${qs}`, {
        method: "POST",
        body: { analysis: vars.analysis },
        lang: vars.lang,
      });
    },
    enabled: vars !== null,
    retry: false,
    staleTime: Infinity,
    ...options,
  });
}

// --- Feasibility + tenant B2C tunable presets ------------------------

/** One tenant-tunable customer option (merged across capable machines).
 *  Mirrors the BE ``b2c_presets_schema`` ``widget:"tunable"`` value plus the
 *  display metadata the feasibility endpoint enriches in. */
export interface TunablePreset {
  presets: { value: number; label_cs?: string; label_en?: string }[];
  default?: number | null;
  allow_slider?: boolean;
  unit?: string;
  title?: string;
  value_type?: "number" | "integer";
}

export interface FeasibilityResponse {
  orderable: boolean;
  /** Per-key union: legacy preset lists, scalar flags wrapped as ``[{value}]``,
   *  or a tunable wrapped as ``[TunablePreset]``. */
  presets: Record<string, unknown[]>;
}

export interface FeasibilityVars {
  process_type: string;
  dim_x_mm?: number | null;
  dim_y_mm?: number | null;
  dim_z_mm?: number | null;
  material_code?: string | null;
  /** Generic part facts. A machine schema's ``feasibility.input`` resolves
   *  against these, so the process's variant axis (declared per process, layer
   *  height, …) rides here under its own declared dimension key — there is
   *  no named axis field. */
  inputs?: Record<string, unknown>;
}

/** Pull the tunable options out of a feasibility response — entries whose
 *  merged value is a single ``TunablePreset`` object (``[{presets, …}]``). */
export function tunablesFromFeasibility(
  resp: FeasibilityResponse | undefined,
): Record<string, TunablePreset> {
  const out: Record<string, TunablePreset> = {};
  if (!resp?.presets) return out;
  for (const [key, bucket] of Object.entries(resp.presets)) {
    const first = Array.isArray(bucket) ? bucket[0] : undefined;
    if (
      first &&
      typeof first === "object" &&
      Array.isArray((first as { presets?: unknown }).presets)
    ) {
      out[key] = first as TunablePreset;
    }
  }
  return out;
}

// --- Srovnání hodnot jedné volby (KAN-1786) -----------------------------

export interface OptionCompareVars {
  process_type: string;
  /** Klíč volby, jejíž hodnoty se srovnávají (autoring operace). */
  field_key: string;
  values: string[];
  /** Řádek jako do ``/pricing/line`` — hodnotu volby dosadí server. */
  line: LineInput & { process_type?: string };
  /** Základ proveditelnosti — hodnotu volby dosadí server. */
  feasibility: FeasibilityVars;
}

export interface OptionCompareItem {
  value: string;
  /** ``null`` = dotaz na proveditelnost selhal (výpadek není verdikt). */
  orderable: boolean | null;
  unit_price_minor: number | null;
  currency_code: string | null;
}

export interface OptionCompareResponse {
  items: OptionCompareItem[];
}

/** Proveditelnost a cena všech hodnot jedné volby JEDNÍM voláním.
 *  Dřív se karty ptaly N× ``/pricing/feasibility`` a N× ``/pricing/line``. */
export function optionCompareQuery(vars: OptionCompareVars) {
  return {
    queryKey: ["pricing", "option-compare", vars] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiFetch<OptionCompareResponse>(`${API}/pricing/option-compare`, {
        method: "POST",
        body: vars,
        signal,
      }),
    retry: false,
  };
}

/** Dotaz na proveditelnost — sdílený hlavním konfigurátorem i kartami voleb. */
export function feasibilityQuery(vars: FeasibilityVars) {
  return {
    queryKey: ["pricing", "feasibility", vars] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiFetch<FeasibilityResponse>(`${API}/pricing/feasibility`, {
        method: "POST",
        body: vars,
        signal,
      }),
    retry: false,
  };
}

export function useFeasibilityPresets(
  vars: FeasibilityVars | null,
  options?: Omit<UseQueryOptions<FeasibilityResponse>, "queryKey" | "queryFn" | "enabled">,
) {
  const { settled, settling } = useSettled(
    vars !== null && Boolean(vars.process_type) ? vars : null,
  );
  const query = useQuery<FeasibilityResponse>({
    ...feasibilityQuery(settled ?? ({} as FeasibilityVars)),
    enabled: settled !== null,
    // Cena musí odpovídat dnešním sazbám: stejný požadavek se sdílí, dokud
    // letí, ale znovu otevřený konfigurátor se ptá znovu.
    staleTime: 0,
    ...options,
  });
  return { ...query, isSettling: settling };
}
